import { Link } from 'react-router-dom';

import clsx from 'clsx';

import { Restricted } from '@/common//Restricted';
import {
  Breadcrumb,
  Button,
  IconButton,
  Label,
  LABEL_TYPE,
  Spinner,
  Tag
} from '@/components/generic';
import {
  BUTTON_TYPE,
  ButtonConfig,
  ButtonVariant
} from '@/components/generic/Button/button-config';
import { IconButtonVariant } from '@/components/generic/IconButton/icon-button-config';
import { PathItem } from '@/types';

type DetailsPageProps = {
  children: React.ReactNode;
  icon: JSX.Element;
  iconPath?: string;
  path: PathItem[];
  title: string;
  tag?: string;
  statusDescription?: string;
  isLoading: boolean;
  childrenClassName?: string;
  mobileHeaderButtons?: ButtonConfig[];
  headerButtons?: ButtonConfig[];
};

export const DetailsPageLayout = ({
  children,
  icon,
  iconPath,
  path,
  title,
  tag,
  statusDescription,
  isLoading,
  childrenClassName = '',
  mobileHeaderButtons = [],
  headerButtons = []
}: DetailsPageProps) => {
  const mapButtons = (buttons: ButtonConfig[]) =>
    buttons?.map((button, index) => {
      if (button.isHidden) {
        return null;
      }
      if (button.type === BUTTON_TYPE.LINK) {
        return (
          <Restricted key={index} to={button.permissions || []}>
            <Link
              to={button.path || '#'}
              target='_blank'
              className={clsx(
                'flex justify-center rounded-[10px] bg-primary-blue p-2 text-base font-bold text-primary-white hover:shadow-primary lg:px-6',
                button.className
              )}>
              {button.label}
            </Link>
          </Restricted>
        );
      }
      if (button.type === BUTTON_TYPE.BUTTON) {
        return (
          <Restricted key={index} to={button.permissions || []}>
            <Button
              variant={button.variant as ButtonVariant}
              handleOnClick={() => button?.handleOnClick && button.handleOnClick()}
              disabled={button.isDisabled}
              className={button.className}>
              {button.label}
            </Button>
          </Restricted>
        );
      }
      if (button.type === BUTTON_TYPE.ICON_BUTTON) {
        return (
          <Restricted key={index} to={button.permissions || []}>
            <IconButton
              variant={button.variant as IconButtonVariant}
              handleOnClick={() => button?.handleOnClick && button.handleOnClick()}
              disabled={button.isDisabled}
              className={button.className || ''}>
              {button.icon}
            </IconButton>
          </Restricted>
        );
      }
      return null;
    });

  return (
    <div className='flex size-full min-h-[calc(100vh-3rem)] flex-col'>
      {isLoading ? (
        <div className='flex h-screen items-center justify-center'>
          <Spinner />
        </div>
      ) : (
        <div className='flex flex-1 flex-col px-3 pt-5 md:gap-9 md:px-20 md:pt-7'>
          <Breadcrumb icon={icon} iconPath={iconPath} path={path} />
          <div className='mb-2.5 w-full md:hidden'>{mapButtons(mobileHeaderButtons)}</div>

          <div className='flex items-center justify-between'>
            <div className='flex max-w-[70%] space-x-2.5 overflow-auto break-all md:space-x-6 md:overflow-hidden'>
              <Label text={title} type={LABEL_TYPE.H2} className='font-semibold' />
              {tag && <Tag value={tag} statusDescription={statusDescription} />}
            </div>
            <div className='flex items-center space-x-2.5 md:space-x-4'>
              {mapButtons(headerButtons)}
            </div>
          </div>
          <div className={clsx('flex flex-col', childrenClassName)}>{children}</div>
        </div>
      )}
    </div>
  );
};
